import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Settings, Lightbulb, CheckCircle2 } from 'lucide-react';

const Home = () => {
  const supportingPoints = [
    { icon: BarChart3, text: 'Accelerate analysis and decision-making' },
    { icon: Settings, text: 'Improve operational efficiency' },
    { icon: Lightbulb, text: 'Turn data into clear, actionable insights' },
    { icon: CheckCircle2, text: 'Execute faster with fewer bottlenecks' },
  ];

  const services = [
    {
      title: 'Strategy',
      tagline: 'Sharper strategy, built faster with better data',
      description:
        'We help you see clearly through complexity. Using AI-enhanced analysis alongside deep operating experience, we identify the opportunities that matter most.',
    },
    {
      title: 'Operations',
      tagline: 'More efficient operations with fewer bottlenecks',
      description:
        'Streamline execution, reduce friction, and improve performance. We bring practical solutions that work in the real world, not just on paper.',
    },
    {
      title: 'Advisory',
      tagline: 'Clear guidance backed by experience and modern tools',
      description:
        'Trusted counsel when stakes are high. We provide direct, honest assessments and help you move forward with confidence.',
    },
  ];

  const howWeWork = [
    {
      step: '01',
      title: 'Understand the Problem',
      description:
        'We quickly assess your situation using structured frameworks and data analysis. No lengthy discovery phases — we get to the heart of the issue.',
    },
    {
      step: '02',
      title: 'Apply AI + Experience',
      description:
        'We use AI tools alongside real operating experience to identify better solutions. Technology enhances judgment; it doesn\'t replace it.',
    },
    {
      step: '03',
      title: 'Execute',
      description:
        'We help you move from idea to action — quickly and effectively. Results matter more than reports.',
    },
  ];

  const clientLogos = [
    { src: 'https://static.wixstatic.com/media/16e4ef_b385b4b3ddf04d769c4e4a2cdf15bd6e~mv2.png/v1/fill/w_305,h_45,al_c,q_85,enc_avif,quality_auto/16e4ef_b385b4b3ddf04d769c4e4a2cdf15bd6e~mv2.png', alt: 'The Connor Group' },
    { src: 'https://static.wixstatic.com/media/16e4ef_4d632db2d55141dd9c45080bffa8549e~mv2.png/v1/fill/w_298,h_45,al_c,lg_1,q_85,enc_avif,quality_auto/16e4ef_4d632db2d55141dd9c45080bffa8549e~mv2.png', alt: 'IMX Solutions' },
    { src: 'https://static.wixstatic.com/media/16e4ef_91f622c40fc54598bb64dd80ac1db344~mv2.png/v1/fill/w_246,h_57,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_91f622c40fc54598bb64dd80ac1db344~mv2.png', alt: 'Nuvei' },
    { src: 'https://static.wixstatic.com/media/16e4ef_9442ebdfc753496ead5b5a3ee37e6e7f~mv2.png/v1/fill/w_146,h_103,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_9442ebdfc753496ead5b5a3ee37e6e7f~mv2.png', alt: 'Edge Innovations' },
    { src: 'https://static.wixstatic.com/media/16e4ef_e06071f624dc4fdaa1c74f3d890e7a14~mv2.png/v1/fill/w_227,h_76,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_e06071f624dc4fdaa1c74f3d890e7a14~mv2.png', alt: 'Triton Submarines' },
    { src: 'https://static.wixstatic.com/media/16e4ef_a49904f169c94d32a288237f4c8fdef9~mv2.png/v1/fill/w_190,h_77,al_c,q_85,enc_avif,quality_auto/16e4ef_a49904f169c94d32a288237f4c8fdef9~mv2.png', alt: 'RBS Resident Billing Services' },
    { src: 'https://static.wixstatic.com/media/16e4ef_9a54066bff4a4972848121e2a7a5e299~mv2.jpg/v1/fill/w_187,h_116,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/image_edited.jpg', alt: 'Fortis' },
    { src: 'https://static.wixstatic.com/media/16e4ef_dbc5e0ed043c40faaf3a19c646b3ea86~mv2.png/v1/fill/w_166,h_95,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_dbc5e0ed043c40faaf3a19c646b3ea86~mv2.png', alt: 'Software Solutions' },
    { src: 'https://static.wixstatic.com/media/16e4ef_3802f611fe9b412191705cabf8efe771~mv2.png/v1/fill/w_429,h_84,al_c,q_85,enc_avif,quality_auto/16e4ef_3802f611fe9b412191705cabf8efe771~mv2.png', alt: 'Paya' },
    { src: 'https://static.wixstatic.com/media/16e4ef_4368ca3df08740d5866e82e7a759f379~mv2.png/v1/fill/w_377,h_84,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_4368ca3df08740d5866e82e7a759f379~mv2.png', alt: 'Spirits' },
    { src: 'https://static.wixstatic.com/media/16e4ef_64d2bfd8d06d440ea2f2b62e6a6b5d89~mv2.png/v1/fill/w_323,h_37,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/16e4ef_64d2bfd8d06d440ea2f2b62e6a6b5d89~mv2.png', alt: 'Windancer Holdings' },
  ];

  return (
    <div data-testid="home-page" className="page-transition">
      {/* Hero Section */}
      <section data-testid="hero-section" className="min-h-screen flex items-center pt-20">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <p className="label-text mb-6 animate-fade-in">
                AI-Enhanced Consulting
              </p>
              <h1 className="heading-1 mb-8 animate-slide-up">
                Better Decisions.
                <br />
                Faster Execution.
                <br />
                <span className="text-gray-400">Powered by AI.</span>
              </h1>
              <p className="body-text text-lg max-w-xl mb-10 animate-slide-up stagger-2">
                We combine decades of real-world operating experience with modern AI tools
                to help organizations solve problems, move faster, and get better results.
              </p>
              
              {/* Supporting Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {supportingPoints.map((point, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 animate-slide-up stagger-${index + 2}`}
                    data-testid={`supporting-point-${index}`}
                  >
                    <point.icon className="w-5 h-5 text-slate-900" />
                    <span className="text-sm text-gray-600">{point.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="hero-cta"
                className="btn-primary inline-flex items-center gap-3 animate-slide-up stagger-5"
              >
                Start a Conversation
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5 animate-fade-in stagger-3">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1771678040857-51d00a11be7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZSUyMHdoaXRlfGVufDB8fHx8MTc3NDUyMjc3M3ww&ixlib=rb-4.1.0&q=85"
                  alt="Modern office space"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section data-testid="positioning-section" className="section-spacing bg-gray-50">
        <div className="container-main">
          <p
            className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 max-w-4xl"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            We use AI as a tool to enhance our work —{' '}
            <span className="text-gray-400">not replace judgment.</span>
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section-spacing">
        <div className="container-main">
          <div className="mb-16">
            <p className="label-text mb-4">What We Do</p>
            <h2 className="heading-2">Our Services</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="service-card"
              >
                <p className="label-text mb-4">0{index + 1}</p>
                <h3 className="heading-3 mb-3">{service.title}</h3>
                <p className="text-slate-900 font-medium mb-4">{service.tagline}</p>
                <p className="body-text">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="services-link"
              className="btn-secondary inline-flex items-center gap-3"
            >
              Learn More
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section data-testid="how-we-work-section" className="section-spacing bg-gray-50">
        <div className="container-main">
          <div className="mb-16">
            <p className="label-text mb-4">Our Approach</p>
            <h2 className="heading-2">How We Work</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {howWeWork.map((item, index) => (
              <div
                key={index}
                data-testid={`how-we-work-step-${index}`}
                className={`lg:col-span-4 ${index === 1 ? 'lg:mt-12' : ''} ${index === 2 ? 'lg:mt-24' : ''}`}
              >
                <div className="bg-white p-8 sm:p-12 border border-gray-200 h-full">
                  <span className="text-6xl font-bold text-gray-100 block mb-6">
                    {item.step}
                  </span>
                  <h3 className="heading-3 mb-4">{item.title}</h3>
                  <p className="body-text">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section data-testid="clients-section" className="section-spacing">
        <div className="container-main">
          <p className="label-text text-center mb-12">
            Working With the Best Clients and Partners
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-8 lg:gap-12 items-center justify-items-center">
            {clientLogos.map((logo, index) => (
              <img
                key={index}
                src={logo.src}
                alt={logo.alt}
                data-testid={`client-logo-${index}`}
                className="client-logo max-h-16 w-auto object-contain"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-10 bg-slate-900 text-white">
        <div className="container-main text-center">
          <h2
            className="text-2xl sm:text-3xl font-medium tracking-tight mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Ready to move faster?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve better outcomes
            with practical AI and proven expertise.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
